"use client";

import { useEffect, useRef } from "react";
import { useSplitTreatments } from "../split/useSplitTreatments";

// eager means right after the initial page load
// note that we'll never link to this URL directly, so there's a 0%
// hit rate, but it loads all editor JS into the browser cache.
const EAGER_RULES = [
  {
    urls: ["/editor"],
    eagerness: "eager",
  },
];

// 30 minutes in milliseconds
const LAST_RENDER_CUTOFF_MS = 30 * 60 * 1000;

/* Adds speculation rules for editor URLs based on split treatement.
 * This is used to pre-render the editor to improve first load times.
 *
 * https://developer.chrome.com/docs/web-platform/prerender-pages
 */
export const useEditorSpeculationRules = () => {
  const rulesLoaded = useRef<HTMLScriptElement | null>(null);
  const { editorSpeculationRules: treatment } = useSplitTreatments();

  useEffect(() => {
    // Test for API support
    // https://developer.chrome.com/docs/web-platform/prerender-pages#detect
    const isSupported =
      HTMLScriptElement.supports &&
      HTMLScriptElement.supports("speculationrules");
    const isEnabled = treatment === "eager";
    const isLoaded = rulesLoaded.current !== null;
    if (!isSupported || !isEnabled || isLoaded) {
      return;
    }

    // The editor sets a timestamp in sessionStorage when it loads
    let editorLastRendered: number | null = null;
    try {
      const sessionLastRendered = globalThis.sessionStorage.getItem(
        "editor-last-rendered",
      );
      editorLastRendered =
        Number.parseInt(sessionLastRendered || "0", 10) || null;
      // eslint-disable-next-line no-empty
    } catch {}
    // If last render was less than the cutoff, don't eagerly prerender
    const lastRenderCutoff = Date.now() - LAST_RENDER_CUTOFF_MS;
    if (editorLastRendered && editorLastRendered > lastRenderCutoff) {
      return;
    }

    const script = document.createElement("script");
    script.type = "speculationrules";
    script.id = "editor-speculation-rules";
    script.innerHTML = JSON.stringify({ prerender: EAGER_RULES });
    document.body.append(script);
    rulesLoaded.current = script;

    // Set a flag in sessionStorage for the editor to read. Lets us easily track across projects.
    try {
      globalThis.sessionStorage.setItem(
        "editor-speculation-rules-flag",
        treatment,
      );
      // eslint-disable-next-line no-empty
    } catch {}
  }, [treatment, rulesLoaded]);
};
